import { useState, useMemo, useCallback, useRef } from "react";
import { VaultConfirmModal } from "./vault/VaultConfirmModal";
import {
  X,
  Layers,
  Calendar,
  Loader2,
  RotateCcw,
  Link2,
  AlertTriangle,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { hasNoUsableClaimedVariations } from "../lib/linkVariations";
import { LinkVariationsModal } from "./LinkVariationsModal";
import { formatCurrency } from "../lib/calculations";
import { useToast } from "./ui/ToastModal";
import {
  type PaymentLineItem,
  type PaymentAction,
  buildPaymentReconciliationInput,
  mergePaymentLineItemsByType,
  savePaymentReconciliation,
  getActionColor,
} from "../lib/paymentLineItems";
import { logActivity } from "../lib/activity-logger";
import type { ClaimsVariation } from "../types/database.types";
import type { ReconciliationStatus } from "../lib/reconciliationStatus";
import {
  type ClaimsReportRow,
  type VariationLineItem,
  MONTH_SHORT,
  shortenDocket,
} from "../lib/paymentPanelShared.types";
import { ActionDropdown } from "../lib/paymentPanelShared";
import {
  calculateDaysBeforeDue,
  stableStringify,
} from "../lib/paymentVersions";
import { logError, logWarn } from "../lib/logError";

/**
 * Maps a payment action to a reconciliation status.
 * Only 'paid' sets the status, other actions leave it as null
 * so the Variations Tab reflects only confirmed payments.
 */
function mapActionToRecon(action: PaymentAction): ReconciliationStatus {
  return action === 'paid' ? 'paid' : null;
}

function actionCountsAsPayment(action: PaymentAction): boolean {
  return action === "paid" || action === "agreement_made";
}

// ─── Props ────────────────────────────────────────────────────
interface VariationPaymentPanelProps {
  report: ClaimsReportRow;
  variations: ClaimsVariation[];
  existingLineItems: PaymentLineItem[];
  onClose: () => void;
  onSaved: () => void;
  onRefresh?: () => void;
}

// ─── Component ────────────────────────────────────────────────
export function VariationPaymentPanel({
  report,
  variations,
  existingLineItems,
  onClose,
  onSaved,
  onRefresh,
}: VariationPaymentPanelProps) {
  const { addToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const period = `${MONTH_SHORT[report.claim_month - 1]} ${report.claim_year}`;

  // Detect legacy report (no usable claimed_set variations)
  const isLegacyReport = hasNoUsableClaimedVariations(report.claimed_set);
  const reportVariationTotal =
    Number(report.variations_this_month ?? 0) +
    Number(report.variations_outstanding ?? 0);
  const currentLinkedIds: string[] = useMemo(() => {
    const vars = report.claimed_set?.variations;
    if (!Array.isArray(vars)) return [];
    return vars
      .filter((v: { variation_id: string }) => !String(v.variation_id ?? "").startsWith("__invoiced"))
      .map((v: { variation_id: string }) => v.variation_id);
  }, [report.claimed_set]);

  // ─── Variation Line Items ──────────────────────────────────
  const [variationLines, setVariationLines] = useState<VariationLineItem[]>(
    () => {
      return variations.map((v) => {
        const total = Number(v.total ?? 0);
        const discount = Number(v.discount_percent ?? 0);
        const effective = discount > 0 ? total * (1 - discount / 100) : total;
        const approved = Number(v.approved_amount ?? 0);
        void (effective > 0
          ? Math.round((approved / effective) * 100 * 100) / 100
          : 0);

        const existing = existingLineItems.find(
          (li) => li.line_type === "variation" && li.variation_id === v.id
        );

        if (existing) {
          return {
            variation_id: v.id,
            variation_name: shortenDocket(v.docket_no),
            claimed_amount: effective,
            effective_amount: effective,
            paid_pct: existing.paid_percentage,
            paid_amount: existing.paid_amount,
            action: existing.action,
            agreed_amount: existing.agreed_amount ?? null,
            notes: existing.notes ?? "",
          };
        }

        // No line item for THIS report, pre-fill from variation's approved_amount
        // so the paid total matches what was actually paid
        if (approved > 0) {
          const pct = effective > 0
            ? Math.round((approved / effective) * 100 * 100) / 100
            : 0;
          return {
            variation_id: v.id,
            variation_name: shortenDocket(v.docket_no),
            claimed_amount: effective,
            effective_amount: effective,
            paid_pct: pct,
            paid_amount: approved,
            action: "paid" as PaymentAction,
            agreed_amount: null,
            notes: "",
          };
        }

        return {
          variation_id: v.id,
          variation_name: shortenDocket(v.docket_no),
          claimed_amount: effective,
          effective_amount: effective,
          paid_pct: 0,
          paid_amount: 0,
          action: "pending" as PaymentAction,
          agreed_amount: null,
          notes: "",
        };
      });
    }
  );
  const [datePaid, setDatePaid] = useState(report.date_paid ?? "");

  // ─── Computed ─────────────────────────────────────────────
  const totalClaimed = useMemo(
    () => variationLines.reduce((sum, vl) => sum + vl.claimed_amount, 0),
    [variationLines]
  );
  const totalPaid = useMemo(
    () =>
      variationLines.reduce(
        (sum, vl) =>
          sum +
          (actionCountsAsPayment(vl.action) ? vl.paid_amount : 0),
        0
      ),
    [variationLines]
  );

  const actionCount = variationLines.filter((vl) => vl.action !== "paid").length;
  const paymentState = useMemo(
    () => stableStringify({ datePaid, variationLines }),
    [datePaid, variationLines]
  );
  const initialPaymentState = useRef(paymentState);
  const isDirty = paymentState !== initialPaymentState.current;


  // Group: current month vs outstanding from previous months
  const variationMonthMap = useMemo(() => {
    const map = new Map<string, { claim_month: number; claim_year: number }>();
    for (const v of variations) {
      map.set(v.id, { claim_month: v.claim_month, claim_year: v.claim_year });
    }
    return map;
  }, [variations]);

  const currentMonthLines = useMemo(
    () =>
      variationLines.filter((vl) => {
        const info = variationMonthMap.get(vl.variation_id);
        return info && info.claim_month === report.claim_month && info.claim_year === report.claim_year;
      }),
    [variationLines, variationMonthMap, report.claim_month, report.claim_year]
  );

  const outstandingLines = useMemo(
    () =>
      variationLines.filter((vl) => {
        const info = variationMonthMap.get(vl.variation_id);
        return info && !(info.claim_month === report.claim_month && info.claim_year === report.claim_year);
      }),
    [variationLines, variationMonthMap, report.claim_month, report.claim_year]
  );

  // ─── Handlers ─────────────────────────────────────────────
  const updateVariationLine = useCallback(
    (idx: number, updates: Partial<VariationLineItem>) => {
      setVariationLines((prev) =>
        prev.map((line, i) => {
          if (i !== idx) return line;
          const updated = { ...line, ...updates };
          if (updates.action === "paid") {
            // Auto-fill only on fresh selection (allows overpayment)
            if (line.paid_amount === 0 || line.action === "voided" || line.action === "held" || line.action === "works_not_completed" || line.action === "pending") {
              updated.paid_pct = 100;
              updated.paid_amount = line.effective_amount;
            }
          } else if (
            updates.action === "pending" ||
            updates.action === "voided" ||
            updates.action === "held" ||
            updates.action === "works_not_completed"
          ) {
            updated.paid_pct = 0;
            updated.paid_amount = 0;
          } else if (updates.action === "agreement_made") {
            // Keep current paid values, user edits directly
          }
          return updated;
        })
      );
    },
    []
  );

  // ─── Reset ────────────────────────────────────────────────
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const executeReset = async () => {
    setShowResetConfirm(false);
    setSaving(true);
    try {
      const mergedLineItems = mergePaymentLineItemsByType(
        existingLineItems,
        "variation",
        []
      );
      const resetPayload = buildPaymentReconciliationInput({
        report,
        lineItems: mergedLineItems,
        datePaid: report.date_paid,
        daysBeforeDue: report.days_before_due,
        variationPayments: null,
        changesSummary: `Variation payment reset for Claim #${report.claim_number} (${period}).`,
      });

      const { success, error: resetError } = await savePaymentReconciliation(resetPayload);
      if (!success) throw new Error(resetError);

      // Also reset approved_amount → 0 and waiting_amount → total for each variation
      for (const v of variations) {
        const total = Number(v.total ?? 0);
        const discount = Number(v.discount_percent ?? 0);
        const effective = discount > 0 ? total * (1 - discount / 100) : total;
        await supabase
          .from("claims_variations")
          .update({
            approved_amount: 0,
            waiting_amount: effective,
          })
          .eq("id", v.id);
      }

      addToast({
        type: "success",
        title: "Payment Reset",
        message: "Variation payment data has been cleared.",
      });

      // Close panel and refresh parent data so next open gets clean state
      onSaved();
    } catch (err) {
      logError("Error resetting variation payment:", err);
      addToast({
        type: "error",
        title: "Reset Failed",
        message: err instanceof Error ? err.message : "Could not reset payment",
      });
    } finally {
      setSaving(false);
    }
  };

  // ─── Save ─────────────────────────────────────────────────
  const handleSave = async () => {
    if (!isDirty) {
      addToast({ type: "info", title: "No changes to save" });
      return;
    }



    setSaving(true);
    try {
      // Build variation line items, skip pending (no decision made)
      const lineItems: PaymentLineItem[] = variationLines
        .filter((vl) => vl.action !== "pending")
        .map((vl) => {
          // For "paid": if user didn't manually enter amount, auto-fill = claimed
          const paidPct = vl.action === "paid" && vl.paid_pct === 0 ? 100 : vl.paid_pct;
          const paidAmt = vl.action === "paid" && vl.paid_amount === 0
            ? vl.claimed_amount : vl.paid_amount;
          return {
            report_id: report.id,
            line_type: "variation" as const,
            variation_id: vl.variation_id,
            claimed_percentage: 100,
            claimed_amount: vl.claimed_amount,
            paid_percentage: actionCountsAsPayment(vl.action) ? paidPct : 0,
            paid_amount: actionCountsAsPayment(vl.action) ? paidAmt : 0,
            action: vl.action,
            agreed_amount: vl.action === "agreement_made" ? vl.paid_amount : null,
            notes: vl.notes || null,
          };
        });

      const mergedLineItems = mergePaymentLineItemsByType(
        existingLineItems,
        "variation",
        lineItems
      );
      const daysBeforeDue = calculateDaysBeforeDue(datePaid, report.payment_due_date);
      const savePayload = buildPaymentReconciliationInput({
        report,
        lineItems: mergedLineItems,
        datePaid,
        daysBeforeDue,
        changesSummary: `Variation payments for Claim #${report.claim_number} (${period}): ${formatCurrency(totalPaid)} paid across ${variationLines.length} variation(s).`,
      });

      const { success, error: saveError } = await savePaymentReconciliation(savePayload);
      if (!success) throw new Error(saveError);

      // 2. Sync to claims_variations + Push to Dashpivot
      const currentUser = (await supabase.auth.getUser()).data.user;

      for (const vl of variationLines) {
        const variation = variations.find((v) => v.id === vl.variation_id);
        if (!variation) continue;

        const currentApproved = Number(variation.approved_amount ?? 0);
        const effectivePaid = actionCountsAsPayment(vl.action) ? vl.paid_amount : 0;

        // Determine reconciliation status from payment action
        const reconStatus = vl.action !== "pending" ? mapActionToRecon(vl.action) : null;

        // If amounts haven't changed, still update reconciliation status if action was set
        if (Math.abs(effectivePaid - currentApproved) < 0.01) {
          if (reconStatus !== null) {
            console.log(`[VP] Amounts unchanged, updating reconciliation → ${reconStatus}`);
            await supabase
              .from("claims_variations")
              .update({
                reconciliation_status: reconStatus,
                reconciled_by: reconStatus ? currentUser?.id ?? null : null,
                reconciled_at: reconStatus ? new Date().toISOString() : null,
              })
              .eq("id", vl.variation_id);
          } else {
            console.log(`[VP] Skipping variation: amounts unchanged`);
          }
          continue;
        }
        console.log(`[VP] Processing variation: recon=${reconStatus}`);

        const total = Number(variation.total ?? 0);
        const discount = Number(variation.discount_percent ?? 0);
        const effective = discount > 0 ? total * (1 - discount / 100) : total;
        const newApproved = Math.min(effectivePaid, effective);
        const newWaiting = Math.max(0, Math.round((effective - newApproved) * 100) / 100);

        await supabase
          .from("claims_variations")
          .update({
            approved_amount: newApproved,
            waiting_amount: newWaiting,
            reconciliation_status: reconStatus,
            reconciled_by: reconStatus ? currentUser?.id ?? null : null,
            reconciled_at: reconStatus ? new Date().toISOString() : null,
          })
          .eq("id", vl.variation_id);

        // Push to Dashpivot (same as ProjectDetail.handleApprovalChange)
        if (variation.dashpivot_form_id) {
          console.log(`[VP] Pushing to Dashpivot`);
          try {
            const { error: pushError } = await supabase.functions.invoke(
              "push-variation-update",
              {
                body: {
                  dashpivotFormId: variation.dashpivot_form_id,
                  approvedAmount: Math.round(newApproved * 100) / 100,
                  projectId: report.project_id,
                },
              },
            );
            if (pushError) {
              logWarn(`[VP] Dashpivot push FAILED:`, pushError);
            } else {
              console.log(`[VP] Dashpivot push SUCCESS`);
            }
          } catch (pushErr) {
            logWarn(`[VP] Dashpivot push ERROR:`, pushErr);
          }
        } else {
          console.log(`[VP] Skipping Dashpivot push: no dashpivot_form_id`);
        }
      }

      // 3. Activity logging
      const actionSummaries: string[] = [];
      for (const vl of variationLines) {
        if (vl.action !== "paid") {
          const label =
            vl.action === "voided" ? "Voided"
              : vl.action === "agreement_made" ? "Agreement Made"
                : "Works Not Completed";
          actionSummaries.push(`${vl.variation_name}: ${label}`);
        }
      }

      await logActivity({
        projectId: report.project_id,
        actionType: "UPDATE",
        entityType: "PAYMENT",
        entityId: report.id,
        description: actionSummaries.length > 0
          ? `Variation payments for Claim #${report.claim_number} (${period}): ${formatCurrency(totalPaid)} paid. Actions: ${actionSummaries.join("; ")}`
          : `Variation payments updated for Claim #${report.claim_number} (${period}): ${formatCurrency(totalPaid)} paid`,
        metadata: {
          claim_number: report.claim_number,
          panel: "variations",
          total_claimed: totalClaimed,
          total_paid: totalPaid,
          variation_count: variationLines.length,
        },
      });

      addToast({
        type: "success",
        title: "Variation Payments Saved",
        message: `${variationLines.length} variation(s): ${formatCurrency(totalPaid)} paid`,
      });
      onSaved();
    } catch (err) {
      logError("Error saving variation payments:", err);
      addToast({
        type: "error",
        title: "Save Failed",
        message: err instanceof Error ? err.message : "Could not save variation payments",
      });
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 20,
          width: "100%",
          maxWidth: 520,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 25px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(139,92,246,0.06)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "18px 24px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "linear-gradient(135deg, #a78bfa, #7c3aed)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(139,92,246,0.3)",
              }}
            >
              <Layers size={18} color="#fff" />
            </div>
            <div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "1rem",
                  color: "#0f172a",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Variations
                <span
                  style={{
                    fontSize: "0.62rem",
                    fontWeight: 600,
                    color: "#8b5cf6",
                    background: "#f5f3ff",
                    padding: "1px 6px",
                    borderRadius: 8,
                  }}
                >
                  {variationLines.length}
                </span>
                {actionCount > 0 && (
                  <span
                    style={{
                      fontSize: "0.62rem",
                      fontWeight: 600,
                      color: "#d97706",
                      background: "#fef3c7",
                      padding: "1px 6px",
                      borderRadius: 8,
                    }}
                  >
                    {actionCount} action{actionCount !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
              <div style={{ fontSize: "0.72rem", color: "#64748b" }}>
                Claim #{report.claim_number} • {period}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#64748b",
              padding: 6,
              borderRadius: 8,
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Legacy warning banner (text only) */}
        {isLegacyReport && (
          <div
            style={{
              margin: "0 16px",
              marginTop: 10,
              padding: "8px 14px",
              borderRadius: 10,
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: "0.72rem",
              color: "#1e40af",
            }}
          >
            <AlertTriangle size={14} style={{ flexShrink: 0, color: "#3b82f6" }} />
            <span>
              <span style={{ fontWeight: 600 }}>No linked variations</span>
              <span style={{ color: "#3b82f6" }}>
                {" "}(using date-based lookup). Use Link below to fix.
              </span>
            </span>
          </div>
        )}

        {/* Scrollable Body */}
        <div style={{ overflow: "auto", flex: 1 }}>
          {variationLines.length === 0 ? (
            <div
              style={{
                padding: "40px 24px",
                textAlign: "center",
                color: "#94a3b8",
                fontSize: "0.85rem",
              }}
            >
              No variations found for this claim period.
            </div>
          ) : (
            <div>
              {/* Link + Reset buttons row */}
              <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6, padding: "8px 24px", marginBottom: 4 }}>
                <button
                  type="button"
                  onClick={() => setShowLinkModal(true)}
                  style={{
                    padding: "3px 10px",
                    borderRadius: 6,
                    border: "1px solid #e2e8f0",
                    background: "#f8fafc",
                    color: "#64748b",
                    fontSize: "0.68rem",
                    fontWeight: 500,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <Link2 size={10} /> {isLegacyReport ? "Link" : "Re-link"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowResetConfirm(true)}
                  disabled={saving}
                  style={{
                    padding: "3px 10px",
                    borderRadius: 6,
                    border: "1px solid #e2e8f0",
                    background: "#f8fafc",
                    color: "#64748b",
                    fontSize: "0.68rem",
                    fontWeight: 500,
                    cursor: saving ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    opacity: saving ? 0.5 : 1,
                  }}
                >
                  <RotateCcw size={10} /> Reset
                </button>
              </div>
              {/* Table header */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 80px 80px minmax(110px, auto)",
                  gap: 6,
                  padding: "8px 24px",
                  background: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  color: "#64748b",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                <span>Variation</span>
                <span style={{ textAlign: "right" }}>Claimed</span>
                <span style={{ textAlign: "right" }}>Paid $</span>
                <span style={{ textAlign: "right" }}>Action</span>
              </div>
              {/* Current month variations */}
              {currentMonthLines.map((vl) => {
                const idx = variationLines.indexOf(vl);
                const actionColors = getActionColor(vl.action);
                return (
                  <div
                    key={vl.variation_id}
                    style={{
                      padding: "12px 24px",
                      borderBottom: "1px solid #f1f5f9",
                      background: vl.action !== "paid" ? actionColors.bg : "transparent",
                      transition: "background 0.15s",
                    }}
                  >
                  <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 80px 80px minmax(110px, auto)",
                        gap: 6,
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: "0.82rem",
                            color: "#0f172a",
                          }}
                        >
                          {vl.variation_name}
                        </div>

                      </div>

                      {/* Claimed */}
                      <div data-seq-private="" style={{ textAlign: "right", fontSize: "0.72rem", color: "#64748b", fontWeight: 500 }}>
                        {formatCurrency(vl.claimed_amount)}
                      </div>

                      {/* Paid: EDITABLE */}
                      <div style={{ textAlign: "right" }}>
            <input data-seq-private
              type="number"
              value={actionCountsAsPayment(vl.action) ? vl.paid_amount : 0}
              disabled={!actionCountsAsPayment(vl.action)}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const amt = raw === "" ? 0 : parseFloat(raw);
                            if (isNaN(amt)) return;
                            const pct = vl.effective_amount > 0
                              ? Math.round((amt / vl.effective_amount) * 100 * 100) / 100
                              : 0;
                            updateVariationLine(idx, {
                              paid_amount: amt,
                              paid_pct: pct,
                            });
                          }}
                          style={{
                            width: "100%",
                            padding: "4px 4px",
                border: `1px solid ${!actionCountsAsPayment(vl.action) ? "#e2e8f0" : "#d1d5db"}`,
                            borderRadius: 5,
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            textAlign: "right",
                            outline: "none",
                background: !actionCountsAsPayment(vl.action) ? "#f1f5f9" : "#fff",
                color: !actionCountsAsPayment(vl.action)
                              ? "#94a3b8"
                              : vl.paid_amount >= vl.claimed_amount
                                ? "#16a34a"
                                : "#d97706",
                            boxSizing: "border-box" as const,
                          }}
                          step="0.01"
                          min="0"
                        />
                      </div>

                      {/* Action dropdown */}
                      <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <ActionDropdown
                          value={vl.action}
                          onChange={(action) => updateVariationLine(idx, { action })}
                        />
                      </div>
                    </div>

                  </div>
                );
              })}

              {/* Outstanding variations from previous months */}
              {outstandingLines.length > 0 && (
                <>
                  <div
                    style={{
                      padding: "8px 24px",
                      background: "linear-gradient(135deg, #fef3c7 0%, #fff7ed 100%)",
                      borderTop: "2px solid #f59e0b",
                      borderBottom: "1px solid #e2e8f0",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span style={{ fontSize: "0.7rem", fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      ⚠ Outstanding from previous months
                    </span>
                    <span style={{ fontSize: "0.62rem", fontWeight: 600, color: "#d97706", background: "#fff", padding: "1px 6px", borderRadius: 8 }}>
                      {outstandingLines.length}
                    </span>
                  </div>
                  {outstandingLines.map((vl) => {
                    const idx = variationLines.indexOf(vl);
                    const actionColors = getActionColor(vl.action);
                    const info = variationMonthMap.get(vl.variation_id);
                    const monthLabel = info ? `${MONTH_SHORT[info.claim_month - 1]}-${String(info.claim_year).slice(-2)}` : "";
                    return (
                      <div
                        key={vl.variation_id}
                        style={{
                          padding: "12px 24px",
                          borderBottom: "1px solid #f1f5f9",
                          background: vl.action !== "paid" ? actionColors.bg : "transparent",
                          transition: "background 0.15s",
                        }}
                      >
                      <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 80px 80px minmax(110px, auto)",
                            gap: 6,
                            alignItems: "center",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontWeight: 600,
                                fontSize: "0.82rem",
                                color: "#0f172a",
                              }}
                            >
                              {vl.variation_name}
                            </div>

                            <div style={{ fontSize: "0.6rem", color: "#d97706", fontWeight: 600 }}>
                              from {monthLabel}
                            </div>
                          </div>

                          {/* Claimed */}
                          <div data-seq-private="" style={{ textAlign: "right", fontSize: "0.72rem", color: "#64748b", fontWeight: 500 }}>
                            {formatCurrency(vl.claimed_amount)}
                          </div>

                          {/* Paid: EDITABLE */}
                          <div style={{ textAlign: "right" }}>
                            <input data-seq-private
                              type="number"
                              value={vl.action === "voided" || vl.action === "held" || vl.action === "works_not_completed" ? 0 : vl.paid_amount}
                              disabled={vl.action === "voided" || vl.action === "held" || vl.action === "works_not_completed"}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const amt = raw === "" ? 0 : parseFloat(raw);
                                if (isNaN(amt)) return;
                                const pct = vl.effective_amount > 0
                                  ? Math.round((amt / vl.effective_amount) * 100 * 100) / 100
                                  : 0;
                                updateVariationLine(idx, {
                                  paid_amount: amt,
                                  paid_pct: pct,
                                });
                              }}
                              style={{
                                width: "100%",
                                padding: "4px 4px",
                                border: `1px solid ${vl.action === "voided" || vl.action === "held" || vl.action === "works_not_completed" ? "#e2e8f0" : "#d1d5db"}`,
                                borderRadius: 5,
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                textAlign: "right",
                                outline: "none",
                                background: vl.action === "voided" || vl.action === "held" || vl.action === "works_not_completed" ? "#f1f5f9" : "#fff",
                                color: vl.action === "voided" || vl.action === "held" || vl.action === "works_not_completed"
                                  ? "#94a3b8"
                                  : vl.paid_amount >= vl.claimed_amount
                                    ? "#16a34a"
                                    : "#d97706",
                                boxSizing: "border-box" as const,
                              }}
                              step="0.01"
                              min="0"
                            />
                          </div>

                          {/* Action dropdown */}
                          <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <ActionDropdown
                              value={vl.action}
                              onChange={(action) => updateVariationLine(idx, { action })}
                            />
                          </div>
                        </div>

                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>

        {/* Date Paid */}
        <div style={{ padding: "0 24px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <Calendar size={13} color="#64748b" />
            <span style={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748b" }}>
              Date Paid
            </span>
          </div>
          <input
            type="date"
            value={datePaid}
            onChange={(e) => setDatePaid(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              fontSize: "0.82rem",
              color: "#0f172a",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ fontSize: "0.72rem", color: "#64748b" }}>
            <strong data-seq-private="" style={{ color: "#0f172a" }}>{formatCurrency(totalPaid, true)}</strong> of{" "}
            {formatCurrency(totalClaimed, true)}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={onClose}
              style={{
                padding: "8px 16px",
                borderRadius: 10,
                border: "1px solid #e2e8f0",
                background: "#f8fafc",
                color: "#64748b",
                fontSize: "0.82rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !isDirty}
              style={{
                padding: "8px 20px",
                borderRadius: 10,
                border: "none",
                background: saving || !isDirty
                  ? "#94a3b8"
                  : "linear-gradient(135deg, #a78bfa, #7c3aed)",
                color: "#fff",
                fontSize: "0.82rem",
                fontWeight: 600,
                cursor: saving || !isDirty ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                boxShadow: saving ? "none" : "0 2px 8px rgba(139,92,246,0.3)",
              }}
            >
              {saving && <Loader2 size={14} className="spin" />}
              {saving
                ? "Saving..."
                : !isDirty
                  ? "No Changes"
                  : "Save Variations"}
            </button>
          </div>
        </div>
      </div>

      {/* Reset Confirmation Modal */}
      <VaultConfirmModal
        isOpen={showResetConfirm}
        title="Reset Variation Payments"
        message="This will delete all saved variation payment data. You can re-enter payment data after resetting."
        confirmLabel="Reset"
        variant="warning"
        onConfirm={executeReset}
        onCancel={() => setShowResetConfirm(false)}
      />
      {/* Link Variations Modal */}
      {showLinkModal && (
        <LinkVariationsModal
          reportId={report.id}
          claimNumber={report.claim_number}
          claimMonth={report.claim_month}
          claimYear={report.claim_year}
          projectId={report.project_id}
          reportVariationTotal={reportVariationTotal}
          currentLinkedIds={currentLinkedIds}
          onClose={() => setShowLinkModal(false)}
          onLinked={() => {
            setShowLinkModal(false);
            if (onRefresh) {
              onRefresh(); // refresh data without closing panel
            } else {
              onSaved();
            }
          }}
        />
      )}
    </div>
  );
}
